import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, Subject } from 'rxjs';
import { User } from '../models/user';
import { IGenericJSON } from '../helpers/interface';
import { of, shareReplay } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class UserService {
  /** The currently authenticated user, populated from cookie or API. */
  currentUser?: User;

  private currentUser$: Observable<User> | null = null;
  /** Internal subject that fires whenever the current user is updated. */
  private userUpdated = new Subject<void>();

  /** Observable stream that emits whenever the current user record changes. */
  userUpdated$ = this.userUpdated.asObservable();

  private http: HttpClient = inject(HttpClient);

  constructor() {
    this.#initializeUserFromCookie();
  }

  /**
   * Fetches the current authenticated user from the API, updates `currentUser`,
   * and emits on `userUpdated$`.
   * @returns {Observable<User>}
   */
  getCurrentUser(): Observable<User> {
    // If user already loaded from cookie, return immediately
    if (this.currentUser) {
      return of(this.currentUser);
    }

    if (this.currentUser$) return this.currentUser$;

    this.currentUser$ = this.http.get<IGenericJSON>('/api/users/currentUser').pipe(
      map((data) => {
        if (!data) throw new Error('No user returned');
        this.currentUser = User.digest(data);
        this.userUpdated.next();
        return this.currentUser;
      }),
      shareReplay(1),
    );

    return this.currentUser$;
  }

  /**
   * Fetches all user records.
   * @returns {Observable<User[]>}
   */
  getAllUsers(): Observable<User[]> {
    return this.http
      .get<IGenericJSON[]>('/api/users')
      .pipe(map((data) => User.digestResponse(data)));
  }

  /**
   * Updates a user record by ID. If the updated user is the current user,
   * refreshes `currentUser` and emits on `userUpdated$`.
   * @param {number} id - The ID of the user to update.
   * @param {Partial<User>} data - The fields to update.
   * @returns {Observable<User>}
   */
  createUser(payload: Record<string, unknown>): Observable<User> {
    return this.http.post<IGenericJSON>(`/api/users`, payload).pipe(
      map((updated) => {
        if (this.currentUser?.id) {
          this.currentUser = User.digest(updated);
          this.userUpdated.next();
        }
        return this.currentUser!;
      }),
    );
  }

  /**
   * Updates a user record by ID. If the updated user is the current user,
   * refreshes `currentUser` and emits on `userUpdated$`.
   * @param {number} id - The ID of the user to update.
   * @param {Partial<User>} data - The fields to update.
   * @returns {Observable<User>}
   */
  updateUser(id: number, data: Partial<User>): Observable<User> {
    return this.http.put<IGenericJSON>(`/api/users/${id}`, data).pipe(
      map((updated) => {
        if (this.currentUser?.id === id) {
          this.currentUser = User.digest(updated);
          this.userUpdated.next();
        }
        return this.currentUser!;
      }),
    );
  }
  /**
   * Clears current user for logout
   */
  clearCurrentUser(): void {
    this.currentUser = undefined;
    this.currentUser$ = null;
  }

  /**
   * Deactivates a user account by ID.
   * @param {number} id - The ID of the user to deactivate.
   * @returns {Observable<{ message: string }>}
   */
  deactivateUser(id: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`/api/users/${id}`);
  }

  /**
   * Returns true if a valid user session exists in the cookie.
   * @returns {boolean}
   */
  isAuthenticated(): boolean {
    return this.#initializeUserFromCookie();
  }

  /**
   * Returns true if the current user has a user type assigned.
   * @returns {boolean}
   */
  hasUserType(): boolean {
    return !!this.currentUser?.userType;
  }

  /**
   * Reads and parses the user data cookie (`cu`) from `document.cookie`.
   * Returns null if the cookie is absent or malformed.
   * @returns {IGenericJSON | null}
   */
  static grabUserDataFromCookie(): IGenericJSON | null {
    if (typeof document === 'undefined') return null;
    for (const val of document.cookie.split(';')) {
      if (val.indexOf('cu') !== -1) {
        const raw = val.split('=')[1];
        if (!raw) return null;
        try {
          return JSON.parse(decodeURIComponent(raw));
        } catch {
          return null;
        }
      }
    }
    return null;
  }

  /**
   * Attempts to hydrate `currentUser` from the cookie on service initialization.
   * Returns false if the cookie is missing or the user has no role assigned.
   * @returns {boolean}
   */
  #initializeUserFromCookie(): boolean {
    const userJSON = UserService.grabUserDataFromCookie();

    if (!userJSON) return false;

    if (!userJSON['user_type']) {
      return false;
    }

    this.currentUser = User.digest(userJSON);
    return true;
  }

  /**
   * Records the current user's agreement to the data privacy policy.
   * Updates the local currentUser record with the agreement timestamp
   * and emits on userUpdated$ so components can react.
   * FERPA §99.7 / COPPA — required acknowledgment of privacy rights.
   * @returns {Observable<{ message: string, agreedAt: string }>}
   */
  agreeToPrivacyPolicy(): Observable<{ message: string; agreedAt: string }> {
    return this.http.post<{ message: string; agreedAt: string }>('/api/users/agree', {}).pipe(
      map((response) => {
        if (this.currentUser) {
          this.currentUser.agreementAgreedAt = response.agreedAt;
          this.userUpdated.next();
        }
        return response;
      }),
    );
  }

  refreshCurrentUser(): Observable<User> {
    this.currentUser$ = null;
    this.currentUser = undefined;
    return this.getCurrentUser();
  }
}
